/**
 * 🏷️ BRANDED TYPES - The Idioma Club
 * ======================================
 * Nominal type system for IDs to prevent accidental mixing.
 * 
 * Usage:
 *   function getUser(id: UserId): Promise<User> { ... }
 *   function getCohort(id: CohortId): Promise<Cohort> { ... }
 *   
 *   // TypeScript will error if you pass a CohortId where UserId is expected!
 *   const userId = 'abc' as UserId;
 *   const cohortId = 'xyz' as CohortId;
 *   getUser(cohortId); // ❌ Type Error!
 */

declare const __brand: unique symbol;
type Brand<T, B> = T & { readonly [__brand]: B };

// ============================================
// CORE ID TYPES
// ============================================

/** UUID identifying a User/Profile */
export type UserId = Brand<string, 'UserId'>;

/** UUID identifying a Cohort (learning group) */
export type CohortId = Brand<string, 'CohortId'>;

/** UUID identifying a Course */
export type CourseId = Brand<string, 'CourseId'>;

/** UUID identifying a Board Task (project board) */
export type BoardTaskId = Brand<string, 'BoardTaskId'>;

/** UUID identifying a Lesson */
export type LessonId = Brand<string, 'LessonId'>;

/** UUID identifying a Tenant (organization) */
export type TenantId = Brand<string, 'TenantId'>;

/** UUID identifying a Session (class meeting) */
export type SessionId = Brand<string, 'SessionId'>;

/** UUID identifying a Subscription */
export type SubscriptionId = Brand<string, 'SubscriptionId'>;

/** UUID identifying a Subscription Plan */
export type PlanId = Brand<string, 'PlanId'>;

/** UUID identifying an Enrollment Survey */
export type SurveyId = Brand<string, 'SurveyId'>;

/** UUID identifying an Enrollment Analytics session */
export type EnrollmentAnalyticsId = Brand<string, 'EnrollmentAnalyticsId'>;

/** UUID identifying an AI Intelligence Insight */
export type InsightId = Brand<string, 'InsightId'>;

/** UUID identifying a Churn Prediction record */
export type ChurnPredictionId = Brand<string, 'ChurnPredictionId'>;

/** UUID identifying an Exercise Attempt */
export type AttemptId = Brand<string, 'AttemptId'>;

/** String tag identifying a Skill (e.g. 'Present_Perfect') */
export type SkillId = Brand<string, 'SkillId'>;

/** UUID identifying a Teacher Review */
export type ReviewId = Brand<string, 'ReviewId'>;

/** UUID identifying a Manual Receipt */
export type ReceiptId = Brand<string, 'ReceiptId'>;

/** UUID identifying a Payment record */
export type PaymentId = Brand<string, 'PaymentId'>;

/** UUID identifying a Coupon */
export type CouponId = Brand<string, 'CouponId'>;
export type RefundId = Brand<string, 'RefundId'>;

/** UUID identifying a Class Slot (bookable class engine slot) */
export type ClassSlotId = Brand<string, 'ClassSlotId'>;

/** UUID identifying a Class Booking */
export type ClassBookingId = Brand<string, 'ClassBookingId'>;

/** UUID identifying a Class Waitlist Entry */
export type ClassWaitlistEntryId = Brand<string, 'ClassWaitlistEntryId'>;

/** UUID identifying a Class Proposal */
export type ClassProposalId = Brand<string, 'ClassProposalId'>;

/** UUID identifying a Class Level */
export type ClassLevelId = Brand<string, 'ClassLevelId'>;

/** `unit-02/episode-02a` — an Episode in the authoring contract.
 *
 *  Not a UUID, and deliberately not a template literal type: the pattern
 *  belongs to the validator, not to the type. `parseEpisodeId`
 *  (`packages/episode-kit/src/domain/ids.ts`) owns the regex and is the only
 *  door in. A template literal here would compile away and leave a string
 *  read from a JSON file indistinguishable from a checked one. */
export type EpisodeId = Brand<string, 'EpisodeId'>;

/** `unit-02` — the method unit an Episode belongs to. Derived from the
 *  EpisodeId, never carried as a second field that can disagree with it. */
export type UnitId = Brand<string, 'UnitId'>;

// ============================================
// HELPER FACTORIES (Runtime casting)
// ============================================

/** Create a UserId from a string (unsafe - for interfaces with external data) */
export const asUserId = (id: string): UserId => id as UserId;

/** Create a CohortId from a string */
export const asCohortId = (id: string): CohortId => id as CohortId;

/** Create a CourseId from a string */
export const asCourseId = (id: string): CourseId => id as CourseId;

/** Create a BoardTaskId from a string */
export const asBoardTaskId = (id: string): BoardTaskId => id as BoardTaskId;

/** Create a LessonId from a string */
export const asLessonId = (id: string): LessonId => id as LessonId;

/** Create a TenantId from a string */
export const asTenantId = (id: string): TenantId => id as TenantId;

/** Create a SessionId from a string */
export const asSessionId = (id: string): SessionId => id as SessionId;

/** Create a SubscriptionId from a string */
export const asSubscriptionId = (id: string): SubscriptionId => id as SubscriptionId;

/** Create a PlanId from a string */
export const asPlanId = (id: string): PlanId => id as PlanId;

/** Create a SurveyId from a string */
export const asSurveyId = (id: string): SurveyId => id as SurveyId;

/** Create an EnrollmentAnalyticsId from a string */
export const asEnrollmentAnalyticsId = (id: string): EnrollmentAnalyticsId => id as EnrollmentAnalyticsId;

/** Create a ChurnPredictionId from a string */
export const asChurnPredictionId = (id: string): ChurnPredictionId => id as ChurnPredictionId;

/** Create an AttemptId from a string */
export const asAttemptId = (id: string): AttemptId => id as AttemptId;

/** Create a SkillId from a string */
export const asSkillId = (id: string): SkillId => id as SkillId;

/** Create a ReviewId from a string */
export const asReviewId = (id: string): ReviewId => id as ReviewId;

/** Create a ReceiptId from a string */
export const asReceiptId = (id: string): ReceiptId => id as ReceiptId;

/** Create a PaymentId from a string */
export const asPaymentId = (id: string): PaymentId => id as PaymentId;

/** Create a CouponId from a string */
export const asCouponId = (id: string): CouponId => id as CouponId;

/** Create a RefundId from a string */
export const asRefundId = (id: string): RefundId => id as RefundId;

/** Create a ClassSlotId from a string */
export const asClassSlotId = (id: string): ClassSlotId => id as ClassSlotId;

/** Create a ClassBookingId from a string */
export const asClassBookingId = (id: string): ClassBookingId => id as ClassBookingId;

/** Create a ClassWaitlistEntryId from a string */
export const asClassWaitlistEntryId = (id: string): ClassWaitlistEntryId => id as ClassWaitlistEntryId;

/** Create a ClassProposalId from a string */
export const asClassProposalId = (id: string): ClassProposalId => id as ClassProposalId;

/** Create a ClassLevelId from a string */
export const asClassLevelId = (id: string): ClassLevelId => id as ClassLevelId;

/* No `asEpisodeId` / `asUnitId` on purpose. The other ids here are
   UUIDs minted by Postgres, so an unchecked cast only re-labels a value that
   was already valid. An EpisodeId arrives from a filename, a form field or a
   model's answer, and the whole reason §4.1 of the Constructor spec exists is
   that two hand-kept id spaces overlapped. `parseEpisodeId` returns a Result
   and is the only way to obtain one. */
/** UUID identifying a Finance Movement (expense, partner contribution or reimbursement) */
export type FinMovementId = Brand<string, 'FinMovementId'>;

/** UUID identifying a Finance Category */
export type FinCategoryId = Brand<string, 'FinCategoryId'>;

/** UUID identifying a Finance Partner (socio) */
export type FinPartnerId = Brand<string, 'FinPartnerId'>;

/** Create a FinMovementId from a string */
export const asFinMovementId = (id: string): FinMovementId => id as FinMovementId;

/** Create a FinCategoryId from a string */
export const asFinCategoryId = (id: string): FinCategoryId => id as FinCategoryId;

/** Create a FinPartnerId from a string */
export const asFinPartnerId = (id: string): FinPartnerId => id as FinPartnerId;

// ============================================
// TYPE GUARDS
// ============================================

/** Check if a string looks like a valid UUID */
export const isUUID = (id: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

/** Validate and create a UserId (returns null if invalid) */
export const validateUserId = (id: string): UserId | null =>
    isUUID(id) ? asUserId(id) : null;
